import { Loader2 } from 'lucide-react';
import { inputCls, type DeductionItem } from '../WithdrawModal.types';

type AmountStepProps = {
  amount: string;
  onAmountChange: (value: string) => void;
  error: string;
  breakdown: DeductionItem[];
  net: number | null;
  totalDeduction: number | null;
  isCalculatingCharges?: boolean;
};

export function AmountStep({
  amount,
  onAmountChange,
  error,
  breakdown,
  net,
  totalDeduction,
  isCalculatingCharges = false,
}: AmountStepProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-xs font-medium text-[var(--dash-muted)]">
          Amount
        </label>
        <input
          type="number"
          inputMode="numeric"
          value={amount}
          min={1000}
          max={100000}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '') {
              onAmountChange('');
              return;
            }
            const numVal = Number(val);
            if (numVal > 100000) {
              onAmountChange('100000');
              return;
            }
            if (numVal < 0) {
              onAmountChange('');
              return;
            }
            onAmountChange(val);
          }}
          placeholder="Enter amount"
          className={inputCls(Boolean(error))}
        />
        {error ? (
          <p className="mt-1 text-[11px] text-red-500">{error}</p>
        ) : (
          <p className="mt-1 text-[11px] text-[var(--dash-muted)]">Min 1,000 · Max 100,000</p>
        )}
      </div>

      {isCalculatingCharges ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-amber-900">Deduction breakdown</p>
            <div className="flex items-center gap-1.5 text-xs text-amber-800">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-700" />
              <span>Calculating deductions…</span>
            </div>
          </div>
          {breakdown.length > 0 ? (
            <div className="mt-2 space-y-1 opacity-50 pointer-events-none transition-opacity">
              {breakdown.map((item, i) => (
                <div key={i} className="flex justify-between py-1 text-xs text-amber-900/80">
                  <span>
                    {item.label}
                    {item.rate != null ? ` (${item.rate}%)` : ''}
                  </span>
                  <span className="font-medium text-red-500">- {item.deduction}</span>
                </div>
              ))}
              {totalDeduction != null ? (
                <div className="mt-2 flex justify-between border-t border-amber-200 pt-2 text-sm font-semibold text-amber-950">
                  <span>Total deduction</span>
                  <span>{totalDeduction}</span>
                </div>
              ) : null}
              {net != null ? (
                <div className="mt-2 flex justify-between text-sm font-semibold text-amber-950">
                  <span>Final payout</span>
                  <span>{net}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : breakdown.length > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
          <p className="mb-2 text-xs font-semibold text-amber-900">Deduction breakdown</p>
          {breakdown.map((item, i) => (
            <div key={i} className="flex justify-between py-1 text-xs text-amber-900/80">
              <span>
                {item.label}
                {item.rate != null ? ` (${item.rate}%)` : ''}
              </span>
              <span className="font-medium text-red-500">- {item.deduction}</span>
            </div>
          ))}

          {totalDeduction != null ? (
            <div className="mt-2 flex justify-between border-t border-amber-200 pt-2 text-sm font-semibold text-amber-950">
              <span>Total deduction</span>
              <span>{totalDeduction}</span>
            </div>
          ) : null}

          {net != null ? (
            <div className="mt-2 flex justify-between text-sm font-semibold text-amber-950">
              <span>Final payout</span>
              <span>{net}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
